import React from "react";

import { useGetCurrentCompanyApplicationQuery, useResolvePendingItemMutation } from "EmployerApplication/api";
import { addEmployeesURL } from "EmployerApplication/urls";
import createDecorator from "final-form-focus";
import { quotePageURL } from "QuotePage/urls";
import { Form } from "react-final-form";
import { useHistory } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { validateRequired } from "Utils/Validations";

import { CardSection } from "Common/Card";
import { SIFinalFormField, SIFormRadioGroup, SIFormTextArea } from "Common/forms";
import { SaveButton } from "Common/forms/buttons";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import { usePartnerContext } from "Common/PartnerContext";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import { Body2, SubBody } from "Theme/BodyText";
import Button from "Theme/Button";

import { getLabelCopy, getOptions } from "./OwnerEnrollmentUtils";

import makeBem from "Utils/makeBem";

const bem = makeBem("ApplicationStepCard");
const focusOnErrors = createDecorator();

const makeCarrierList = (alternativeCarriers) => {
  const listItems = alternativeCarriers.map((carrier) => (
    <li key={carrier.brand_name} style={{ paddingBottom: "15px" }}>
      <img
        src={carrier.logo}
        title={carrier.brand_name}
        alt={carrier.brand_name}
        className="PlanName__logo"
        style={{ maxHeight: "38px", paddingBottom: "5px" }}
      />

      {carrier.caveat && (
        <SubBody classicProps={{ style: { fontSize: "12px" } }} intuitProps={{ style: { fontSize: "12px" } }}>
          {`Note: ${carrier.caveat}`}
        </SubBody>
      )}
    </li>
  ));

  return listItems;
};

const OwnerEnrollment = ({ pendingItemPayload, scrollToEmployeeApplications }) => {
  const { data: companyApplication, isLoading } = useGetCurrentCompanyApplicationQuery();
  const [resolvePendingItem] = useResolvePendingItemMutation();
  const history = useHistory();
  const companyPolicyId = companyApplication?.id;

  const ownerPendingItemType = pendingItemPayload.owner_issue_type;
  const ownerName = pendingItemPayload.owner_name;

  let allowsDecliningEmployees;
  let alternativeCarriers = [];

  if (!isLoading) {
    allowsDecliningEmployees = companyApplication.allowsDecliningEmployees;
    alternativeCarriers = companyApplication.alternativeCarriersForOwnerEnrollment;
  }

  const enrolleeName = ownerName || "the enrollee";

  const notOwnerOrSpouseLabel =
    ownerPendingItemType === "owner_and_spouse"
      ? "Please explain the relationship between the two enrollees:"
      : `Please explain ${enrolleeName}'s role and relationship to the owner so that we can pass the information along to the insurance company:`;
  const notOwnerOrSpouseText = (
    <Row>
      <SIFinalFormField
        name="ownerOnlyExplanation"
        label={notOwnerOrSpouseLabel}
        component={SIFormTextArea}
        validate={validateRequired}
        style={{ marginBottom: "0" }}
        componentProps={{
          maxLength: "255",
        }}
      />
    </Row>
  );

  const selectAlternativePlanText = (
    <Div>
      <Body2>
        The following carriers will allow a company to enroll with just owners:
        <ul style={{ listStyle: "none", padding: "15px 15px 0 0" }}>{makeCarrierList(alternativeCarriers)}</ul>
        <Button
          color="gray"
          size="small"
          style={{ minWidth: "258px" }}
          onClick={() => {
            AnalyticsTracking.trackEvent("cta_clicked", {
              location: "owner_enrollment_page",
              action: "change_plan",
            });

            window.location.replace(appendAuthParams(quotePageURL()));
          }}
        >
          Choose a Different Plan
        </Button>
      </Body2>
    </Div>
  );

  const { supportPhone } = usePartnerContext();
  const ownerIsOnlyEnrolleeText = (
    <Body2 color="error">
      Unfortunately, you will not qualify for the small group health insurance plan you chose. If you have questions, please live chat with an agent below, or
      call {supportPhone}.
    </Body2>
  );

  const submitHandler = async (values) => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "owner_enrollment_page",
      action: "submit_explanation",
    });

    let resolutionType;

    switch (ownerPendingItemType) {
      case "single_owner":
        resolutionType = "owner_only_enrollee";
        break;
      case "multiple_owners":
        resolutionType = "multiple_owner_enrollees";
        break;
      case "owner_and_spouse":
        resolutionType = "owner_and_spouse_enrollees";
        break;
      default:
        throw "Undefined owner pending item type";
    }

    await resolvePendingItem({
      companyPolicyId,
      type: "owner",
      resolution_type: resolutionType,
      explanation_text: values.ownerOnlyExplanation,
    });
  };

  const onAddClicked = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "owner_enrollment_page",
      action: "add_new_enrollee",
    });

    history.push(addEmployeesURL);
  };

  const onEditClicked = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "owner_enrollment_page",
      action: "enroll_existing_employee",
    });

    scrollToEmployeeApplications();
  };

  return (
    <Form decorators={[focusOnErrors]} onSubmit={submitHandler}>
      {({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit}>
          <CardSection>
            <Row>
              <SIFinalFormField
                name="ownerOnlyResolution"
                label={<Div className={bem("PendingItemsLabel")}>{getLabelCopy(ownerPendingItemType, allowsDecliningEmployees)}</Div>}
                component={SIFormRadioGroup}
                componentProps={{
                  options: getOptions(ownerPendingItemType, ownerName, alternativeCarriers, allowsDecliningEmployees),
                }}
                className={bem(null, "pendingItemRadios")}
              />
            </Row>
            {values.ownerOnlyResolution == "notOwnerOrSpouse" && notOwnerOrSpouseText}
            {values.ownerOnlyResolution == "modifyEmployeesEnrollingInMedical" &&
              (allowsDecliningEmployees ? (
                <Button color="gray" size="small" style={{ minWidth: "258px", marginTop: "20px" }} onClick={onAddClicked}>
                  Add an Employee
                </Button>
              ) : (
                <Row>
                  <SIFinalFormField
                    name="ownerOnlyModifyEmployeesResolution"
                    component={SIFormRadioGroup}
                    label="You can add another employee, or edit the application of an employee who previously declined coverage."
                    componentProps={{
                      options: {
                        addEmployee: "I will add another employee who will enroll in coverage",
                        enrollMoreEmployees: "An employee who previously declined coverage will enroll",
                      },
                    }}
                    className={bem(null, "pendingItemRadios")}
                  />

                  {values.ownerOnlyModifyEmployeesResolution == "addEmployee" && (
                    <Button color="gray" size="small" style={{ minWidth: "258px" }} onClick={onAddClicked}>
                      Add a New Enrollee
                    </Button>
                  )}

                  {values.ownerOnlyModifyEmployeesResolution == "enrollMoreEmployees" && (
                    <Button color="gray" size="small" style={{ minWidth: "258px" }} onClick={onEditClicked}>
                      Edit Existing Employee Applications
                    </Button>
                  )}
                </Row>
              ))}

            {values.ownerOnlyResolution == "selectAlternativePlan" && selectAlternativePlanText}
            {values.ownerOnlyResolution == "ownerIsOnlyEnrollee" && ownerIsOnlyEnrolleeText}
          </CardSection>
          <Row>
            <Col>
              <SaveButton submitting={submitting} buttonText="Submit" disabled={!values.ownerOnlyResolution == "notOwnerOrSpouse"} size="small" mt={3} />
            </Col>
          </Row>
        </form>
      )}
    </Form>
  );
};

export default OwnerEnrollment;
